/**
 * @file FormulationScreenshotModal.js
 * @version 1.3.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-08-14
 * @description Read a formulation off a screenshot, then approve it row by row
 *              before it reaches the form.
 *
 *              Three ways in — drop a file, pick one, or paste an image.
 *              A screenshot, a PDF, a spreadsheet or a Word document: all four
 *              are read and none is stored.
 *              straight from the clipboard, which is how most of these arrive
 *              (Cmd-Shift-4, then Cmd-V). There was no upload component in the
 *              app to reuse, so this one is built here.
 *
 *              Nothing is applied automatically. Every extracted figure is a
 *              row the reviewer can untick or retype, values that would
 *              overwrite something already typed are flagged, and rows the
 *              extractor couldn't place are listed rather than dropped. A
 *              misread decimal in an inclusion rate is not a mistake anyone
 *              wants applied silently, so the approval step is the point.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import TickCheckbox from './TickCheckbox';
import Dropdown from './Dropdown';
import { useSlideOutButton } from './SlideOutButton';
import './Formulations.css';

// Sections, in the order a sheet presents them: what goes in, then what comes
// out. Matches the edit form the approved figures land in.
const GROUPS = [
  { key: 'ingredients', title: 'Ingredients' },
  { key: 'analysis', title: 'Analysis' }
];

const CONFIDENCE_COPY = {
  high: { icon: 'check_circle', text: 'Read cleanly' },
  medium: { icon: 'info', text: 'Check the figures before approving' },
  low: { icon: 'warning', text: 'Low confidence — check every figure carefully' }
};

/**
 * What may be read: an image, a PDF, a spreadsheet or a Word document. A
 * screenshot is still the common case, but a supplier's spec arrives as often
 * as a PDF or a workbook, and the server reads all four the same way.
 *
 * Checked on extension as well as MIME type: a .docx dragged from some file
 * managers arrives with an empty or generic type.
 */
const ACCEPTED_TYPES = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const ACCEPTED_EXTENSIONS = /\.(png|jpe?g|gif|webp|pdf|xlsx?|docx)$/i;

function isAcceptedFile(file) {
  if (!file) return false;
  return ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXTENSIONS.test(file.name || '');
}

function ReviewBody({
  stage, error, preview, result, rows, setRows, newRows, setNewRows,
  existingValues, components, onPickFile, onDrop
}) {
  useSlideOutButton([stage, rows.length, newRows.length]);

  const labelFor = useCallback(
    (key) => components.find((c) => c.key === key)?.label || key, [components]);

  // A key the registry no longer knows falls under Ingredients rather than
  // vanishing between the two sections.
  const groupOf = useCallback(
    (key) => components.find((c) => c.key === key)?.component_group || 'ingredients',
    [components]);

  if (stage !== 'review') {
    return (
      <div className="fm-shot-body">
        {error && <div className="formulation-error">{error}</div>}

        <div
          className={`fm-shot-drop${stage === 'reading' ? ' busy' : ''}`}
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('over'); }}
          onDragLeave={(e) => e.currentTarget.classList.remove('over')}
          onDrop={(e) => { e.currentTarget.classList.remove('over'); onDrop(e); }}
          onClick={stage === 'reading' ? undefined : onPickFile}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPickFile(); }}
        >
          {stage === 'reading' ? (
            <>
              <span className="material-symbols-rounded spinning">progress_activity</span>
              <span className="fm-shot-drop-title">Reading the formulation…</span>
              <span className="fm-shot-drop-hint">This usually takes a few seconds.</span>
            </>
          ) : (
            <>
              <span className="material-symbols-rounded">add_photo_alternate</span>
              <span className="fm-shot-drop-title">Drop a file here, or click to choose one</span>
              <span className="fm-shot-drop-hint">
                A screenshot, PDF, spreadsheet or Word document. Nothing is stored — the file is
                read and dropped. You can also paste an image — <kbd>Cmd</kbd>/<kbd>Ctrl</kbd> + <kbd>V</kbd> —
                anywhere in this window.
                PNG, JPEG, GIF or WebP, up to 10 MB.
              </span>
            </>
          )}
        </div>

        {preview && (
          <div className="fm-shot-preview">
            <img src={preview} alt="The screenshot being read" />
          </div>
        )}
      </div>
    );
  }

  const conf = CONFIDENCE_COPY[result.confidence] || CONFIDENCE_COPY.medium;
  const chosen = rows.filter((r) => r.use).length;

  return (
    <div className="fm-shot-body fm-shot-review">
      {/* Two panels: the figures to approve on the left, and everything about
          the read — verdict, the image it came from, what it couldn't place —
          on the right. The table is the work; the rest is reference you glance
          at while doing it, so it doesn't belong above the table pushing it
          down the modal. */}
      <div className="fm-shot-main">
        {error && <div className="formulation-error">{error}</div>}

        {/* Split the way the sheet is: ingredients are inclusions that should
            total 100, analysis are properties of the result. Two lists that
            happen to share a column layout, not one list of eighteen rows.
            A group with nothing read in it is omitted rather than shown empty. */}
        {GROUPS.map(({ key: group, title }) => {
          const groupRows = rows
            .map((row, i) => ({ row, i }))
            .filter(({ row }) => groupOf(row.key) === group);
          if (!groupRows.length) return null;

          return (
            <section key={group} className="fm-shot-group">
              <h4 className="fm-shot-group-title">{title}</h4>
              <table className="data-table fm-shot-table">
                <thead>
                  <tr>
                    <th className="fm-shot-use">Use</th>
                    <th>Component</th>
                    <th className="num">Current</th>
                    <th className="num">Read</th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map(({ row, i }) => {
                    const current = existingValues[row.key];
                    const overwrites = current !== undefined && current !== '' &&
                      String(current) !== String(row.value);
                    return (
                      <tr key={row.key} className={overwrites ? 'overwrites' : ''}>
                        <td className="fm-shot-use">
                          <TickCheckbox
                            id={`fm-shot-${row.key}`}
                            checked={row.use}
                            ariaLabel={`Apply ${labelFor(row.key)}`}
                            onChange={() => setRows(rows.map((r, j) =>
                              j === i ? { ...r, use: !r.use } : r))}
                          />
                        </td>
                        <td>
                          {labelFor(row.key)}
                          {overwrites && (
                            <span className="fm-shot-overwrite" title="This would replace a value already on the form">
                              replaces {current}
                            </span>
                          )}
                        </td>
                        <td className="num muted">{current ?? '—'}</td>
                        <td className="num">
                          <Input
                            type="number"
                            inputMode="decimal"
                            value={row.value}
                            onChange={(e) => setRows(rows.map((r, j) =>
                              j === i ? { ...r, value: e.target.value, use: true } : r))}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          );
        })}

        <p className="fm-shot-summary">
          {chosen} of {rows.length} will be applied to the form. Nothing is saved until you
          save the formulation itself.
        </p>
      </div>

      <aside className="fm-shot-aside">
        <div className={`fm-shot-verdict conf-${result.confidence}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span className="material-symbols-rounded">{conf.icon}</span>
            <strong>{conf.text}</strong>
          </div>
          <div className="fm-shot-verdict-note">
            {rows.length} figure{rows.length === 1 ? '' : 's'} read.
            {result.notes ? ` ${result.notes}` : ''}
          </div>
        </div>

        {preview && (
          <div className="fm-shot-preview compact">
            <img src={preview} alt="The screenshot that was read" />
          </div>
        )}

        {newRows.length > 0 && (
          <div className="fm-shot-unmatched">
            <span className="material-symbols-rounded">help</span>
            <div>
              <strong>Not recognised</strong>
              <span className="fm-shot-unmatched-hint">
                Tick to add as a new field. It joins the registry, so it becomes
                available on every formulation — check the spelling and the group
                before you do.
              </span>

              <table className="data-table fm-shot-new-table">
                <thead>
                  <tr>
                    <th className="fm-shot-use">Add</th>
                    <th>Name</th>
                    <th>Group</th>
                    <th className="num">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {newRows.map((row, i) => (
                    <tr key={i} className={row.use ? 'accepted' : ''}>
                      <td className="fm-shot-use">
                        <TickCheckbox
                          id={`fm-shot-new-${i}`}
                          checked={row.use}
                          ariaLabel={`Add ${row.label} as a new field`}
                          onChange={() => setNewRows(newRows.map((r, j) =>
                            j === i ? { ...r, use: !r.use } : r))}
                        />
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={row.label}
                          onChange={(e) => setNewRows(newRows.map((r, j) =>
                            j === i ? { ...r, label: e.target.value } : r))}
                        />
                      </td>
                      <td>
                        <Dropdown
                          options={GROUPS.map(g => ({ value: g.key, label: g.title }))}
                          value={row.group}
                          onChange={(v) => setNewRows(newRows.map((r, j) =>
                            j === i ? { ...r, group: v } : r))}
                        />
                      </td>
                      <td className="num">
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={row.value}
                          onChange={(e) => setNewRows(newRows.map((r, j) =>
                            j === i ? { ...r, value: e.target.value } : r))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

export default function FormulationScreenshotModal({
  components, existingValues = {}, onClose, onApply, onCreateComponent
}) {
  const [stage, setStage] = useState('pick');   // pick | reading | review
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  // Rows the extractor couldn't place, offered as new registry fields.
  const [newRows, setNewRows] = useState([]);
  const [applying, setApplying] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const send = useCallback(async (file) => {
    if (!file) return;
    setError('');
    setStage('reading');
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(file); });

    try {
      const body = new FormData();
      body.append('image', file);
      // No scope sent: every component is offered on every formulation now, so
      // narrowing what the extractor may map to would only push figures into
      // "unrecognised" that have a perfectly good field waiting for them.

      const res = await fetch(`${API_URL}/api/formulations/extract`, {
        method: 'POST', credentials: 'include', body
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not read that image');

      const entries = Object.entries(data.values || {});
      if (!entries.length) {
        setError('No formulation figures were found in that image. Try a clearer or closer crop.');
        setStage('pick');
        return;
      }

      // Sheet order, not registry order: the reviewer is checking these against
      // the image next to them, so the two should read top-to-bottom together.
      // `data.order` is the sequence the figures were read in; anything missing
      // from it (an older server, say) falls in behind, in whatever order the
      // values arrived.
      const read = new Map((data.order || []).map((key, i) => [key, i]));
      setResult(data);
      // Default to NOT adding: creating a registry field affects every
      // formulation, so it should be a decision, not something that happens
      // because someone clicked Apply without reading.
      setNewRows((data.unmatched || []).map((u) => ({
        label: u.label,
        value: String(u.value ?? ''),
        // Which section of the sheet it came from, so an analysis figure
        // doesn't arrive pre-labelled as an ingredient. Older servers don't
        // send it; ingredients is the commoner case.
        group: u.group === 'analysis' ? 'analysis' : 'ingredients',
        use: false
      })));
      setRows(entries
        .sort((a, b) => (read.get(a[0]) ?? 9999) - (read.get(b[0]) ?? 9999))
        .map(([key, value]) => ({ key, value: String(value), use: true })));
      setStage('review');
    } catch (e) {
      setError(e.message);
      setStage('pick');
    }
  }, []);

  // Paste is how most screenshots arrive, so it's wired to the whole window
  // while the modal is open rather than to a single focused field.
  useEffect(() => {
    const onPaste = (e) => {
      const item = [...(e.clipboardData?.items || [])]
        .find((i) => i.type.startsWith('image/'));
      if (!item) return;
      e.preventDefault();
      send(item.getAsFile());
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [send]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const file = [...(e.dataTransfer?.files || [])].find((f) => isAcceptedFile(f));
    if (file) send(file);
    else setError('That file is not an image. Use PNG, JPEG, GIF or WebP.');
  }, [send]);

  const apply = useCallback(async () => {
    const values = {};
    for (const row of rows) {
      if (!row.use) continue;
      const n = Number(row.value);
      if (Number.isFinite(n)) values[row.key] = String(n);
    }

    // Accepted unmatched rows become registry components first, then their
    // values join the rest. Created one at a time rather than in parallel: the
    // route de-duplicates keys by reading the existing set, so concurrent
    // creates of similar labels could race to the same key.
    const toCreate = newRows.filter((r) => r.use && r.label.trim());
    if (toCreate.length && onCreateComponent) {
      setApplying(true);
      try {
        for (const row of toCreate) {
          const created = await onCreateComponent(row.label.trim(), row.group);
          if (typeof created === 'string') {
            setError(`Could not add "${row.label.trim()}": ${created}`);
            setApplying(false);
            return;
          }
          const n = Number(row.value);
          if (created?.key && Number.isFinite(n)) values[created.key] = String(n);
        }
      } finally {
        setApplying(false);
      }
    }

    onApply(values);
  }, [rows, newRows, onApply, onCreateComponent]);

  const chosen = useMemo(() => rows.filter((r) => r.use).length, [rows]);
  const accepted = useMemo(
    () => newRows.filter((r) => r.use && r.label.trim()).length, [newRows]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Read a formulation from a screenshot"
      size="lg"
      className="formulation-screenshot"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          {stage === 'review' && (
            <div>
              <button className="btn-slideout" onClick={() => { setStage('pick'); setResult(null); setRows([]); setNewRows([]); }}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">restart_alt</span></div>
                <span className="btn-label">Try another</span>
              </button>
            </div>
          )}
          <div>
            <button className="btn-slideout" onClick={apply} disabled={stage !== 'review' || !chosen}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
              <span className="btn-label">
                {applying ? 'Adding fields…'
                  : stage === 'review' ? `Apply ${chosen + accepted} to the form` : 'Apply'}
              </span>
            </button>
          </div>
        </div>
      }
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,.pdf,.xlsx,.xls,.docx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: 'none' }}
        onChange={(e) => { send(e.target.files?.[0]); e.target.value = ''; }}
      />
      <ReviewBody
        stage={stage}
        error={error}
        preview={preview}
        result={result}
        rows={rows}
        setRows={setRows}
        newRows={newRows}
        setNewRows={setNewRows}
        existingValues={existingValues}
        components={components}
        onPickFile={() => fileRef.current?.click()}
        onDrop={onDrop}
      />
    </Modal>
  );
}
